/**
 * Regras de validação para códigos baseadas no cargo/posição do usuário
 *
 * Regras:
 * - Operador: entre 7 e 9 dígitos
 * - Outros cargos: até 4 dígitos
 */

export interface CodeValidationRule {
  exactLength?: number;
  minLength?: number;
  maxLength?: number;
  pattern: RegExp;
  message: string;
}

export interface CodeValidationRules {
  [position: string]: CodeValidationRule;
}

export const CODE_VALIDATION_RULES: CodeValidationRules = {
  // Operador: mínimo 7 e máximo 9 dígitos
  'operador': {
    minLength: 7,
    maxLength: 9,
    pattern: /^\d{7,9}$/,
    message: 'O código da lotérica deve ter entre 7 e 9 dígitos'
  },
  'operador loterica': {
    minLength: 7,
    maxLength: 9,
    pattern: /^\d{7,9}$/,
    message: 'O código da lotérica deve ter entre 7 e 9 dígitos'
  },
  'operador de loterica': {
    minLength: 7,
    maxLength: 9,
    pattern: /^\d{7,9}$/,
    message: 'O código da lotérica deve ter entre 7 e 9 dígitos'
  },
  
  // Outros cargos: até 4 dígitos
  'gerente': {
    maxLength: 4,
    pattern: /^\d{1,4}$/,
    message: 'O código deve ter até 4 dígitos'
  },
  'supervisor': {
    maxLength: 4,
    pattern: /^\d{1,4}$/,
    message: 'O código deve ter até 4 dígitos'
  },
  'superintendente': {
    maxLength: 4,
    pattern: /^\d{1,4}$/,
    message: 'O código deve ter até 4 dígitos'
  },
  'dono': {
    maxLength: 4,
    pattern: /^\d{1,4}$/,
    message: 'O código deve ter até 4 dígitos'
  },
  'dono de loterica': {
    maxLength: 4,
    pattern: /^\d{1,4}$/,
    message: 'O código deve ter até 4 dígitos'
  },
  'proprietario': {
    maxLength: 4,
    pattern: /^\d{1,4}$/,
    message: 'O código deve ter até 4 dígitos'
  },
  
  // Regra padrão para qualquer outro cargo
  'default': {
    maxLength: 4,
    pattern: /^\d{1,4}$/,
    message: 'O código deve ter até 4 dígitos'
  }
};

/**
 * Obtém a regra de validação para um cargo específico
 * @param position - Cargo/posição do usuário (case insensitive)
 * @returns Regra de validação correspondente ou regra padrão
 */
export const getValidationRuleForPosition = (position: string): CodeValidationRule => {
  const normalizedPosition = position?.toLowerCase().trim() || '';
  
  // Busca por match exato primeiro
  if (CODE_VALIDATION_RULES[normalizedPosition]) {
    return CODE_VALIDATION_RULES[normalizedPosition];
  }
  
  // Busca por match parcial para operador
  if (normalizedPosition.includes('operador')) {
    return CODE_VALIDATION_RULES.operador;
  }
  
  // Retorna regra padrão se não encontrar match
  return CODE_VALIDATION_RULES.default;
};

/**
 * Valida um código baseado na posição do usuário
 * @param code - Código a ser validado
 * @param position - Cargo/posição do usuário
 * @returns Objeto com resultado da validação e mensagem de erro (se houver)
 */
export const validateCodeForPosition = (code: string, position: string): {
  isValid: boolean;
  errorMessage?: string;
} => {
  if (!code) {
    return {
      isValid: false,
      errorMessage: 'Código é obrigatório.'
    };
  }
  
  const rule = getValidationRuleForPosition(position);
  const isValid = rule.pattern.test(code);
  
  return {
    isValid,
    errorMessage: isValid ? undefined : rule.message
  };
};
